<script setup lang="ts">
import { inject, nextTick, onMounted, type Ref, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import ProgressSpinner from 'primevue/progressspinner';
import { isAxiosError} from 'axios';
import { http } from '@/plugins/axios';
import DialogTemplate from '@/core/components/common/DialogTemplate.vue';
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions';
import { formatDateTime } from '@/core/lib';
import type { Participation } from '@/modules/recruitment/models.ts';

const dialogRef = inject<Ref<DynamicDialogInstance>>('dialogRef');
const toast = useToast();
const refId = ref();
const results = ref<Participation[]>([]);
const loading = ref(false);
const refIdInputRef = ref();
const moreResults = ref(false);
const hasSearched = ref(false);
const focus = () => {
  nextTick(() => {
    refIdInputRef.value.$el?.focus();
  });
};

onMounted(() => {
  focus();
});
const modalStyles = ref({
  height: '100%',
  width: '100%',
});

const searchParticipants = async () => {
  try {
    results.value = [];
    loading.value = true;
    const resp = await http.get('/participants/find', {
      params: { id: refId.value },
    });
    if (resp.data && resp.data.content) {
      moreResults.value = resp.data.content.length > 25;
      results.value = resp.data.content.slice(0, 25);
      modalStyles.value = { height: '75vh', width: '90vw' };
      hasSearched.value = true;
    }
  } catch (error) {
    if (isAxiosError(error) && error.status === 400) {
      toast.add({
        severity: 'error',
        summary: 'Failed to search',
        detail: 'Invalid search term, please only use id\'s',
        life: 5000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Failed to search',
        detail: error,
        life: 5000,
      });
    }
  } finally {
    loading.value = false;
    focus();
  }
};

const onFindParticipantSubmit = () => {
  searchParticipants();
};

const onFindClick = () => {
  searchParticipants();
};

const linkClicked = () => {
  setTimeout(() => {
    if (dialogRef) {
      dialogRef.value.close();
    }
  }, 200);
};
</script>

<template>
  <DialogTemplate title="Find participant" :footer="moreResults">
    <div class="flex flex-col overflow-hidden h-full" :style="modalStyles">
      <div class="flex-initial">
        <Panel header="Find by ID (full or partial)">
          <form @submit.prevent="onFindParticipantSubmit()" class="relative">
            <InputText
              v-model="refId"
              ref="refIdInputRef"
              :disabled="loading"
              placeholder="Participant Ref, interview ID, etc."
              class="w-96"
            />
            <Button
              :disabled="loading || !refId"
              label="Find"
              icon="pi pi-arrow-right"
              iconPos="right"
              class="ml-5"
              @click="onFindClick()"
            />
            <ProgressSpinner
              style="
                margin-left: 5px;
                width: 40px;
                height: 40px;
                vertical-align: middle;
              "
              v-if="loading"
            />
          </form>
        </Panel>
      </div>
      <div class="flex-1 overflow-hidden">
        <DataTable
          :value="results"
          scrollable
          scrollHeight="flex"
          v-if="hasSearched"
        >
          <Column header="Ref" field="id">
            <template #body="{ data: p }">
              <span class="whitespace-nowrap">{{ p.id }}</span>
            </template>
          </Column>
          <Column header="Name" field="vendorPII.name">
            <template #body="{ data: p }">
              <span class="whitespace-nowrap">{{ p.vendorPII.name }}</span>
            </template>
          </Column>
          <Column header="Status" field="status" />
          <Column header="Date">
            <template #body="{ data: p }">
              <span v-if="p.startedAt" class="whitespace-nowrap">{{
                formatDateTime(p.startedAt)
              }}</span>
            </template>
          </Column>
          <Column header="Job">
            <template #body="{ data: p }">
              <RouterLink
                @click="linkClicked"
                class="text-primary whitespace-nowrap"
                :to="{
                  name: 'recruitment.job.screeners',
                  params: { jobId: p.recruitmentJobId },
                }"
                v-if="p.recruitmentJobId"
                >{{ p.recruitmentJobTitle }}</RouterLink
              >
              <span v-else>--</span>
            </template>
          </Column>
          <Column header="Segment">
            <template #body="{ data: p }">
              <RouterLink
                @click="linkClicked"
                class="text-primary whitespace-nowrap"
                :to="{
                  name: 'recruitment.job.interviews',
                  params: { segmentId: p.segmentId, jobId: p.recruitmentJobId },
                }"
                v-if="p.segmentId"
                >{{ p.segmentTitle }}</RouterLink
              >
              <span v-else>--</span>
            </template>
          </Column>
          <template #empty>
            <div class="italic text-center">No participations found</div>
          </template>
        </DataTable>
      </div>
    </div>
    <template #footer>
      <div v-if="moreResults" class="text-slate-500 dark:text-surface-400 text-center">
        More than {{ results.length }} participants was found.
      </div>
    </template>
  </DialogTemplate>
</template>

<style></style>
